import { createAsyncThunk } from '@reduxjs/toolkit';
import { AppServerResponse } from '../../utils/interfaces/generalInterfaces';
import axios from 'axios';
import { BASE_API_URL } from '../../utils/constants';
import { logoutUser } from './authService';
import { myFirebaseAuth } from '../../firebase/config';
import { SupplierJobUpdate, SupplierJobCreate } from '@dill/dill-shared';
import { firebaseLargeFileUploadHelper } from '../../utils/helpers';

export const updateSupplierJob = createAsyncThunk(
  'supplierJobs/updateSupplierJob',
  async (
    {
      supplierId,
      supplierJobDetails,
      supplierJobId,
      attachmentFiles,
      attachmentUrls,
    }: {
      supplierId: string;
      supplierJobDetails: SupplierJobUpdate;
      supplierJobId: string;
      attachmentFiles?: File[];
      attachmentUrls?: { name: string; url: string }[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const attachments: { name: string; url: string; fileType: string }[] = [];
      if (attachmentFiles && attachmentFiles.length > 0) {
        const storagePathName = `jobs/${supplierJobDetails?.supplierId ?? ''}/${
          supplierJobId ?? ''
        }`;
        for (let i = 0; i < attachmentFiles.length; i++) {
          const file = attachmentFiles[i];
          const fileType = file.name.split('.')[file.name.split('.').length - 1];
          const uploadURL = await firebaseLargeFileUploadHelper({
            blobUrl: URL.createObjectURL(file),
            fileNameWithExtension: `attachment-${Date.now()}.${fileType}`,
            storagePath: storagePathName,
          });
          attachments.push({
            url: uploadURL ?? '',
            name: file.name,
            fileType,
          });
        }
      }
      let jobInfoSheetAttachments: { name: string; url: string; fileType: string }[] = [];
      if (attachmentUrls) {
        jobInfoSheetAttachments = [
          ...jobInfoSheetAttachments,
          ...attachments,
          ...attachmentUrls.map((item) => {
            const fileType = item.name.split('.')[item.name.split('.').length - 1];
            return { fileType, name: item.name, url: item.url };
          }),
        ];
      } else {
        if (attachmentUrls && attachmentUrls)
          jobInfoSheetAttachments = [...jobInfoSheetAttachments, ...attachments];
      }

      let moreDetails = {};
      if (jobInfoSheetAttachments.length > 0) {
        moreDetails = { ...moreDetails, jobInfoSheetAttachments };
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierJobs/updateSupplierJob/${supplierJobId}`,
        { ...supplierJobDetails, ...moreDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const createSupplierJob = createAsyncThunk(
  'supplierJobs/createSupplierJob',
  async (
    {
      supplierId,
      supplierJobDetails,
      attachmentFiles,
    }: {
      supplierId: string;
      supplierJobDetails: SupplierJobCreate;
      attachmentFiles?: File[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierJobs/createSupplierJob`,
        {
          ...supplierJobDetails,
        },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );
      if (response.data.success && response.data?.data?.id) {
        const attachments: { name: string; url: string; fileType: string }[] = [];
        if (attachmentFiles && attachmentFiles.length > 0) {
          const storagePathName = `jobs/${supplierJobDetails?.supplierId ?? ''}/${
            response?.data?.data.id ?? ''
          }`;
          for (let i = 0; i < attachmentFiles.length; i++) {
            const file = attachmentFiles[i];
            const fileType = file.name.split('.')[file.name.split('.').length - 1];
            const uploadURL = await firebaseLargeFileUploadHelper({
              blobUrl: URL.createObjectURL(file),
              fileNameWithExtension: `attachment-${Date.now()}.${fileType}`,
              storagePath: storagePathName,
            });
            attachments.push({
              url: uploadURL ?? '',
              name: file.name,
              fileType,
            });
          }
        }
        if (attachments.length > 0) {
          await thunkApi.dispatch(
            updateSupplierJob({
              supplierId: supplierId,
              supplierJobId: response.data.data.id,
              supplierJobDetails: { jobInfoSheetAttachments: attachments },
            })
          );
        }
      }

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const updateArchiveStateSupplierJob = createAsyncThunk(
  'supplierJobs/updateArchiveStateSupplierJob',
  async (
    {
      supplierId,
      supplierJobsIds,
      markAs,
    }: {
      supplierId: string;
      supplierJobsIds: string[];
      markAs: 'ACTIVE' | 'INACTIVE';
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierJobs/updateArchiveStateSupplierJobs/`,
        { supplierJobsIds, markAs },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);

      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        if (error?.response?.status === 401) {
          thunkApi.dispatch(logoutUser());
        }
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierJobs = createAsyncThunk(
  'supplierJobs/getSupplierJobs',
  async (
    {
      supplierId,
      includeInvoices,
      includePreLiens,
      isOnlyArchivedJobs,
      isFetchAll,
      previousLastDocumentId,
      pageSize,
      sortBy,
      sortOrder,
    }: {
      supplierId: string;
      includeInvoices: boolean;
      includePreLiens: boolean;
      isOnlyArchivedJobs: boolean;
      isFetchAll?: boolean;
      previousLastDocumentId?: string;
      pageSize?: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      isHideLoadingIndicator?: boolean;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let params: any = { isOnlyArchivedJobs };
      if (previousLastDocumentId) {
        params = { ...params, previousLastDocumentId };
      }
      if (sortBy) {
        params = { ...params, sortBy };
      }
      if (pageSize) {
        params = { ...params, pageSize };
      }
      if (sortOrder) {
        params = { ...params, sortOrder };
      }
      if (includeInvoices !== undefined) {
        params = { ...params, includeInvoices };
      }
      if (includePreLiens !== undefined) {
        params = { ...params, includePreLiens };
      }
      if (isFetchAll !== undefined) {
        params = { ...params, isFetchAll };
      }

      const url = `${BASE_API_URL}/suppliers/${supplierId}/supplierJobs/getSupplierJobs`;

      const response = await axios.get<AppServerResponse>(url, {
        params,
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const getSupplierJob = createAsyncThunk(
  'supplierJobs/getSupplierJob',
  async (
    { supplierId, supplierJobId }: { supplierId: string; supplierJobId: string },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }

      const response = await axios.get<AppServerResponse>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierJobs/getSupplierJob/${supplierJobId}`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const recursiveGetSupplierJobs = createAsyncThunk(
  'supplierJobs/recursiveGetSupplierJobs',
  async (
    {
      supplierId,
      includeInvoices,
      includePreLiens,
      isOnlyArchivedJobs,
      isFetchAll,
      previousLastDocumentId,
      pageSize,
      sortBy,
      sortOrder,
      isShowLoadingIndicator = false,
    }: {
      supplierId: string;
      includeInvoices?: boolean;
      includePreLiens?: boolean;
      isShowLoadingIndicator?: boolean;
      isOnlyArchivedJobs: boolean;
      isFetchAll?: boolean;
      previousLastDocumentId?: string;
      pageSize?: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      let params: any = { isOnlyArchivedJobs };
      if (previousLastDocumentId) {
        params = { ...params, previousLastDocumentId };
      }
      if (sortBy) {
        params = { ...params, sortBy };
      }
      if (pageSize) {
        params = { ...params, pageSize };
      }
      if (sortOrder) {
        params = { ...params, sortOrder };
      }
      if (includeInvoices !== undefined) {
        params = { ...params, includeInvoices };
      }
      if (includePreLiens !== undefined) {
        params = { ...params, includePreLiens };
      }
      if (isFetchAll !== undefined) {
        params = { ...params, isFetchAll };
      }

      const url = `${BASE_API_URL}/suppliers/${supplierId}/supplierJobs/getSupplierJobs`;

      const response = await axios.get<AppServerResponse>(url, {
        params,
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });
      return response.data;
    } catch (error: any) {
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const bulkUpdateSupplierJobs = createAsyncThunk(
  'supplierJobs/bulkUpdateSupplierJobs',
  async (
    {
      supplierId,
      supplierJobDetails,
      supplierJobId,
    }: {
      supplierId: string;
      supplierJobDetails: SupplierJobUpdate;
      supplierJobId: string;
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.put<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierJobs/bulkUpdateSupplierJobs/${supplierJobId}`,
        { ...supplierJobDetails },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const supplierJobInfoSheetRequest = createAsyncThunk(
  'supplierJobs/supplierJobInfoSheetRequest',
  async (
    {
      supplierId,
      supplierJobId,
      jobInfoSheetLink,
      jobInfoSheetEmails,
    }: {
      supplierId: string;
      supplierJobId: string;
      jobInfoSheetLink: string;
      jobInfoSheetEmails?: string[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierJobs/supplierJobInfoSheetRequest`,
        { supplierJobId, jobInfoSheetEmails, jobInfoSheetLink },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);

export const mergeSupplierJobs = createAsyncThunk(
  'supplierJobs/mergeSupplierJobs',
  async (
    {
      supplierId,
      supplierJobIdToMergeInto,
      supplierJobIdsToMerge,
    }: {
      supplierId: string;
      supplierJobIdToMergeInto: string;
      supplierJobIdsToMerge: string[];
    },
    thunkApi
  ) => {
    try {
      const token = await myFirebaseAuth.currentUser?.getIdToken();
      if (!token) {
        return null;
      }
      const response = await axios.post<any>(
        `${BASE_API_URL}/suppliers/${supplierId}/supplierJobs/mergeSupplierJobs`,
        { supplierJobIdToMergeInto, supplierJobIdsToMerge },
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.log(error);
      if (error?.response?.status === 401) {
        thunkApi.dispatch(logoutUser());
      } else if (error?.response?.data?.errors && error?.response?.data?.errors.length > 0) {
        return thunkApi.rejectWithValue(error?.response?.data?.errors[0]?.msg);
      }
      return thunkApi.rejectWithValue(error.message);
    }
  }
);
